import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import type { User } from "./types";

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (role: User["role"]) => boolean;
}

const RANK: Record<string, number> = { viewer: -2, analyst: -1, manager: 0, admin: 1 };

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<User>("/auth/me")
      .then((r) => setUser(r.data))
      .catch((e) => {
        // AUTH_ENABLED=false still returns the system principal; a real 401 leaves user null
        if (!(e instanceof ApiError) || e.status !== 401) console.warn("auth/me failed", e);
      })
      .finally(() => setLoading(false));
  }, []);

  const value: AuthState = {
    user,
    loading,
    login: async (username, password) => {
      const r = await api.post<{ user: User }>("/auth/login", { username, password });
      setUser(r.data.user);
    },
    logout: async () => {
      await api.post("/auth/logout").catch(() => undefined);
      setUser(null);
    },
    can: (role) => (user ? RANK[user.role] >= RANK[role] : false),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
